/* =============================================================================
 * Candidate form model — shared by public registration and the admin
 * create/edit screens. One flat, all-strings form state; converted to the
 * exact request bodies the backend accepts (unknown fields are rejected, so
 * the converters below are deliberately strict about what they include).
 * ========================================================================== */

import type {
  AdminCreateRequest,
  AdminUpdateRequest,
  Candidate,
  CandidateDetails,
  CandidateType,
  CollegeDetails,
  CollegeLevel,
  Gender,
  Language,
  OpenDetails,
  QualificationLevel,
  RegisterRequest,
  SchoolDetails,
} from '../types.ts';
import { DISTRICTS, type Group, groupOf } from './enums.ts';
import { ageFrom, isRealDate } from './format.ts';

export interface CandidateFormState {
  name: string;
  dob: string;
  gender: Gender | '';
  language: Language | '';
  phone: string;
  whatsapp_phone: string;
  email: string;
  address: string;
  city: string;
  locality: string;
  district: string;
  state: 'maharashtra';
  pincode: string;
  /** What the candidate picks first: शालेय or खुला. */
  group: Group | '';
  /** school → 'school'; open group → 'college' (student) or 'open' (working), picked in the form. */
  candidate_type: CandidateType | '';
  school: SchoolDetails;
  college: { level: CollegeLevel | ''; year_of_study: string; degree: string; institution_name: string };
  open: { qualification: QualificationLevel | ''; profession: string };
}

export type FormErrors = Partial<Record<string, string>>;
export type FormMode = 'register' | 'admin-create' | 'admin-edit';

export const emptyCandidateForm = (): CandidateFormState => ({
  name: '',
  dob: '',
  gender: '',
  language: '',
  phone: '',
  whatsapp_phone: '',
  email: '',
  address: '',
  city: '',
  locality: '',
  district: '',
  state: 'maharashtra',
  pincode: '',
  group: '',
  candidate_type: '',
  school: {
    grade: '',
    school_name: '',
    school_address: '',
    school_locality: '',
    school_city: '',
    school_district: '',
    school_pincode: '',
    school_phone: '',
    school_email: '',
  },
  college: { level: '', year_of_study: '', degree: '', institution_name: '' },
  open: { qualification: '', profession: '' },
});

export const GRADES = [
  { value: '1st', label: '१ली' },
  { value: '2nd', label: '२री' },
  { value: '3rd', label: '३री' },
  { value: '4th', label: '४थी' },
  { value: '5th', label: '५वी' },
  { value: '6th', label: '६वी' },
  { value: '7th', label: '७वी' },
  { value: '8th', label: '८वी' },
  { value: '9th', label: '९वी' },
  { value: '10th', label: '१०वी' },
  { value: '11th', label: '११वी' },
  { value: '12th', label: '१२वी' },
];
export const gradeLabel = (v: string) => GRADES.find((g) => g.value === v)?.label ?? v;

/* ------------------------------ normalizers ------------------------------ */

/** "+91 98765 43210" → "9876543210". Returns the input unchanged if it can't be normalized. */
export function normalizeMobile(v: string): string {
  let d = v.replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return d;
}
const isMobile = (v: string) => /^[6-9]\d{9}$/.test(normalizeMobile(v));
const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());

/** Picking a group resets the sub-type when needed. */
export function withGroup(f: CandidateFormState, g: Group): CandidateFormState {
  if (g === 'school') return { ...f, group: g, candidate_type: 'school' };
  return { ...f, group: g, candidate_type: f.candidate_type === 'school' ? '' : f.candidate_type };
}

/* ------------------------------- validate -------------------------------- */

export function validateCandidate(f: CandidateFormState, mode: FormMode): FormErrors {
  const e: FormErrors = {};
  const req = (key: string, v: string, msg: string) => {
    if (!v.trim()) e[key] = msg;
  };

  req('name', f.name, 'कृपया पूर्ण नाव भरा.');
  if (!e.name && f.name.trim().length < 2) e.name = 'नाव खूपच लहान आहे.';

  if (!f.dob) e.dob = 'कृपया जन्मतारीख निवडा.';
  else if (!isRealDate(f.dob)) e.dob = 'कृपया वैध जन्मतारीख निवडा.';
  else if (f.group === 'school' && f.dob < '2009-01-01') {
    e.dob = 'शालेय गटासाठी जन्मतारीख १ जानेवारी २००९ रोजी किंवा त्यानंतरची असावी.';
  } else if (f.group === 'open' && (f.dob < '1982-01-01' || f.dob > '2008-12-31')) {
    e.dob = 'खुल्या गटासाठी जन्मतारीख १ जानेवारी १९८२ ते ३१ डिसेंबर २००८ दरम्यान असावी.';
  } else {
    const age = ageFrom(f.dob);
    if (age === null || age < 3 || age > 110) e.dob = 'जन्मतारीख तपासा — वय ३ ते ११० वर्षांदरम्यान असावे.';
  }

  if (!f.gender) e.gender = 'कृपया लिंग निवडा.';
  if (!f.language) e.language = 'कृपया स्पर्धेसाठी निवडलेली भाषा निवडा.';

  if (f.whatsapp_phone.trim() && !isMobile(f.whatsapp_phone)) {
    e.whatsapp_phone = 'कृपया वैध १० अंकी व्हॉट्सअ‍ॅप क्रमांक भरा.';
  }

  if (mode !== 'admin-edit') {
    if (!isMobile(f.phone)) e.phone = 'कृपया वैध १० अंकी मोबाईल क्रमांक भरा.';
    if (f.email.trim() && !isEmail(f.email)) e.email = 'कृपया वैध ईमेल पत्ता भरा.';
    req('address', f.address, 'कृपया पत्ता भरा.');
    req('city', f.city, 'कृपया गाव / शहर भरा.');
    req('locality', f.locality, 'कृपया नगर भरा.');
    if (!DISTRICTS.some((d) => d.value === f.district)) e.district = 'कृपया जिल्हा निवडा.';
    if (!/^[1-9]\d{5}$/.test(f.pincode.trim())) e.pincode = 'कृपया वैध ६ अंकी पिनकोड भरा.';
    if (!f.group) e.group = 'कृपया गट निवडा.';
    else if (!f.candidate_type) e.candidate_type = 'कृपया आपण महाविद्यालयीन विद्यार्थी आहात की नोकरी / व्यवसाय करता ते निवडा.';
  }

  if (f.candidate_type === 'school') {
    if (!f.school.grade) e['school.grade'] = 'कृपया इयत्ता निवडा.';
    req('school.school_name', f.school.school_name, 'कृपया शाळेचे नाव भरा.');
    req('school.school_address', f.school.school_address, 'कृपया शाळेचा पत्ता भरा.');
    req('school.school_locality', f.school.school_locality, 'कृपया शाळेचे नगर भरा.');
    req('school.school_city', f.school.school_city, 'कृपया शाळेचे गाव / शहर भरा.');
    if (!DISTRICTS.some((d) => d.value === f.school.school_district)) e['school.school_district'] = 'कृपया शाळेचा जिल्हा निवडा.';
    if (!/^[1-9]\d{5}$/.test(f.school.school_pincode.trim())) e['school.school_pincode'] = 'कृपया शाळेचा वैध ६ अंकी पिनकोड भरा.';
    const sp = f.school.school_phone.replace(/\D/g, '');
    if (sp.length < 6 || sp.length > 13) e['school.school_phone'] = 'कृपया शाळेचा वैध दूरध्वनी क्रमांक भरा.';
    if (!isEmail(f.school.school_email)) e['school.school_email'] = 'कृपया शाळेचा वैध ईमेल भरा.';
  } else if (f.candidate_type === 'college') {
    if (!f.college.level) e['college.level'] = 'कृपया स्तर निवडा.';
    const y = Number(f.college.year_of_study);
    if (!Number.isInteger(y) || y < 1 || y > 10) e['college.year_of_study'] = 'कृपया अभ्यासाचे वर्ष (१ ते १०) भरा.';
    req('college.degree', f.college.degree, 'कृपया पदवी / अभ्यासक्रमाचे नाव भरा.');
    req('college.institution_name', f.college.institution_name, 'कृपया महाविद्यालयाचे नाव भरा.');
  } else if (f.candidate_type === 'open') {
    if (!f.open.qualification) e['open.qualification'] = 'कृपया शैक्षणिक पात्रता निवडा.';
    req('open.profession', f.open.profession, 'कृपया व्यवसाय भरा.');
  }
  return e;
}

/** Does this error key belong to the "details" (category) section? */
export const isDetailsKey = (k: string) => /^(school|college|open)\./.test(k);

/* ------------------------------ converters ------------------------------- */

function detailsFrom(f: CandidateFormState): CandidateDetails {
  switch (f.candidate_type) {
    case 'school':
      return {
        grade: f.school.grade,
        school_name: f.school.school_name.trim(),
        school_address: f.school.school_address.trim(),
        school_locality: f.school.school_locality.trim(),
        school_city: f.school.school_city.trim(),
        school_district: f.school.school_district,
        school_pincode: f.school.school_pincode.trim(),
        school_phone: f.school.school_phone.trim(),
        school_email: f.school.school_email.trim(),
      };
    case 'college':
      return {
        level: f.college.level as CollegeLevel,
        year_of_study: Number(f.college.year_of_study),
        degree: f.college.degree.trim(),
        institution_name: f.college.institution_name.trim(),
      };
    case 'open':
      return {
        qualification: f.open.qualification as QualificationLevel,
        profession: f.open.profession.trim(),
      };
    default:
      throw new Error('candidate_type not set');
  }
}

function baseCreate(f: CandidateFormState): AdminCreateRequest {
  const body: AdminCreateRequest = {
    name: f.name.trim(),
    dob: f.dob,
    gender: f.gender as Gender,
    language: f.language as Language,
    phone: normalizeMobile(f.phone),
    address: f.address.trim(),
    city: f.city.trim(),
    locality: f.locality.trim(),
    district: f.district,
    state: f.state,
    pincode: f.pincode.trim(),
    candidate_type: f.candidate_type as CandidateType,
    details: detailsFrom(f),
  };
  // Optional fields: omit entirely when blank (spec: "do not send an empty string").
  if (f.email.trim()) body.email = f.email.trim();
  if (f.whatsapp_phone.trim()) body.whatsapp_phone = normalizeMobile(f.whatsapp_phone);
  return body;
}

export const toRegisterRequest = (f: CandidateFormState, turnstileToken: string): RegisterRequest => ({
  ...baseCreate(f),
  accepted_toc_rules: true,
  turnstile_token: turnstileToken,
});

export const toAdminCreateRequest = (f: CandidateFormState): AdminCreateRequest => baseCreate(f);

/**
 * PUT body. Only name/dob/gender/whatsapp_phone/details are editable; we send
 * the full current values (sending an unchanged value is a no-op server-side).
 * A blank WhatsApp number is omitted — the backend cannot clear it anyway.
 */
export function toUpdateRequest(f: CandidateFormState): AdminUpdateRequest {
  const body: AdminUpdateRequest = {
    name: f.name.trim(),
    dob: f.dob,
    gender: f.gender,
    language: f.language,
    details: detailsFrom(f),
  };
  if (f.whatsapp_phone.trim()) body.whatsapp_phone = normalizeMobile(f.whatsapp_phone);
  return body;
}

/** Candidate row + fetched details → form state, for the admin edit screen. */
export function formFromCandidate(c: Candidate, details: CandidateDetails | null): CandidateFormState {
  const f = emptyCandidateForm();
  Object.assign(f, {
    name: c.name,
    dob: c.dob ?? '',
    gender: c.gender,
    language: c.language,
    phone: c.phone,
    whatsapp_phone: c.whatsapp_phone ?? '',
    email: c.email ?? '',
    address: c.address,
    city: c.city,
    locality: c.locality,
    district: c.district,
    pincode: c.pincode,
    group: groupOf(c.candidate_type),
    candidate_type: c.candidate_type,
  });
  if (details) {
    if (c.candidate_type === 'school') f.school = { ...f.school, ...(details as SchoolDetails) };
    if (c.candidate_type === 'college') {
      const d = details as CollegeDetails;
      f.college = {
        level: d.level ?? '',
        year_of_study: d.year_of_study ? String(d.year_of_study) : '',
        degree: d.degree ?? '',
        institution_name: d.institution_name ?? '',
      };
    }
    if (c.candidate_type === 'open') {
      const d = details as OpenDetails;
      f.open = { qualification: d.qualification ?? '', profession: d.profession ?? '' };
    }
  }
  return f;
}
